import { supabase } from '../../../shared/lib/supabase';
import { videoHighlightsService, VideoHighlight } from '../../../shared/services/videoHighlightsService';

export interface ServiceResult<T> {
  success: boolean;
  data?: T;
  error?: string;
}

interface DeleteR2FilesResponse {
  success: boolean;
  results: Array<{
    path: string;
    success: boolean;
    error?: string;
  }>;
  summary: {
    total: number;
    succeeded: number;
    failed: number;
  };
}

/**
 * Service for deleting files from R2 storage and updating database records
 */
export const r2DeletionService = {
  /**
   * Delete highlight assets (stitched and/or enhanced videos)
   */
  async deleteHighlightAssets(
    highlightId: string,
    options: {
      deleteStitched: boolean;
      deleteEnhanced: boolean;
    }
  ): Promise<ServiceResult<{ deletedStitched: boolean; deletedEnhanced: boolean }>> {
    try {
      // Get current session for auth token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        return { success: false, error: 'Not authenticated' };
      }

      // Fetch highlight record
      const highlightResult = await videoHighlightsService.getHighlightById(highlightId);
      if (!highlightResult.success || !highlightResult.data) {
        return { success: false, error: highlightResult.error || 'Highlight not found' };
      }

      const highlight = highlightResult.data;
      const userId = session.user.id;

      // Build array of file paths to delete
      const filePaths: string[] = [];

      if (options.deleteStitched && highlight.output_storage_path) {
        filePaths.push(highlight.output_storage_path);
      }

      if (options.deleteEnhanced && highlight.enhanced_storage_path) {
        filePaths.push(highlight.enhanced_storage_path);
      }

      if (filePaths.length === 0) {
        return { success: false, error: 'No files found to delete' };
      }

      // orgId is optional - edge function can derive it from paths or skip membership check for highlights/tactics
      // We still try to get it for better security, but don't fail if we can't
      let orgId: string | undefined;
      try {
        const matchResult = await supabase
          .from('matches')
          .select('tournament_id, tournaments!inner(organisation_id)')
          .eq('id', highlight.match_id)
          .single();

        if (matchResult.data) {
          orgId = (matchResult.data as any).tournaments?.organisation_id;
        }
      } catch (err) {
        // If we can't get orgId, that's okay - edge function will handle it
        console.warn('Could not retrieve orgId for highlight deletion:', err);
      }

      // Call edge function to delete files from R2
      const { data: deleteResult, error: deleteError } = await supabase.functions.invoke(
        'delete-r2-files',
        {
          body: {
            ...(orgId && { orgId }), // Only include orgId if we have it
            userId,
            filePaths,
          },
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      ) as { data: DeleteR2FilesResponse | null; error: any };

      if (deleteError) {
        console.error('Error calling delete-r2-files:', deleteError);
        // Extract error message from various possible error formats
        let errorMessage = 'Failed to delete files from R2';
        if (deleteError.message) {
          errorMessage = deleteError.message;
        } else if (deleteError.error) {
          errorMessage = typeof deleteError.error === 'string' ? deleteError.error : deleteError.error.message || errorMessage;
        } else if (typeof deleteError === 'string') {
          errorMessage = deleteError;
        }
        return { success: false, error: errorMessage };
      }

      if (!deleteResult) {
        return { success: false, error: 'No response from delete-r2-files function' };
      }

      // Check if all deletions succeeded
      const allSucceeded = deleteResult.success && deleteResult.summary.failed === 0;
      if (!allSucceeded) {
        const failedPaths = deleteResult.results
          .filter((r) => !r.success)
          .map((r) => r.path);
        return {
          success: false,
          error: `Some files could not be deleted: ${failedPaths.join(', ')}`,
        };
      }

      // All R2 deletions succeeded, now update database
      const updates: Partial<VideoHighlight> = {};

      if (options.deleteStitched) {
        updates.output_storage_path = null;
        updates.output_url = null;
        updates.file_size_bytes = 0;
      }

      if (options.deleteEnhanced) {
        updates.enhanced_storage_path = null;
        updates.enhanced_url = null;
        updates.enhanced_file_size_bytes = 0;
      }

      // Determine if we should set is_active = false
      // Set to false only if both videos are being deleted
      if (options.deleteStitched && options.deleteEnhanced) {
        updates.is_active = false;
      } else {
        // Check if the other video still exists
        const hasStitched = options.deleteStitched
          ? false
          : Boolean(highlight.output_storage_path);
        const hasEnhanced = options.deleteEnhanced
          ? false
          : Boolean(highlight.enhanced_storage_path);

        // If neither exists, set is_active = false
        if (!hasStitched && !hasEnhanced) {
          updates.is_active = false;
        }
      }

      // Update all fields directly via Supabase (updateHighlight filters by is_active=true, which fails for inactive highlights)
      const dbUpdatePayload: Record<string, unknown> = {
        updated_at: new Date().toISOString(),
        updated_by: userId,
      };

      // Add storage field updates
      if (updates.output_storage_path !== undefined) {
        dbUpdatePayload.output_storage_path = updates.output_storage_path;
      }
      if (updates.output_url !== undefined) {
        dbUpdatePayload.output_url = updates.output_url;
      }
      if (updates.file_size_bytes !== undefined) {
        dbUpdatePayload.file_size_bytes = updates.file_size_bytes;
      }
      if (updates.enhanced_storage_path !== undefined) {
        dbUpdatePayload.enhanced_storage_path = updates.enhanced_storage_path;
      }
      if (updates.enhanced_url !== undefined) {
        dbUpdatePayload.enhanced_url = updates.enhanced_url;
      }
      if (updates.enhanced_file_size_bytes !== undefined) {
        dbUpdatePayload.enhanced_file_size_bytes = updates.enhanced_file_size_bytes;
      }
      if (updates.is_active !== undefined) {
        dbUpdatePayload.is_active = updates.is_active;
      }

      // Update highlight record directly (don't use updateHighlight as it filters by is_active=true)
      console.log(`[DELETE R2] Updating database for highlight ${highlightId}:`, dbUpdatePayload);
      const { data: updateData, error: updateError } = await supabase
        .from('video_highlights')
        .update(dbUpdatePayload)
        .eq('id', highlightId)
        .select();

      if (updateError) {
        console.error('Error updating highlight after deletion:', updateError);
        return {
          success: false,
          error: `Files deleted from R2 but database update failed: ${updateError.message}`,
        };
      }

      console.log(`[DELETE R2] Database update successful. Updated rows:`, updateData);

      return {
        success: true,
        data: {
          deletedStitched: options.deleteStitched,
          deletedEnhanced: options.deleteEnhanced,
        },
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error in deleteHighlightAssets:', errorMessage);
      return { success: false, error: errorMessage };
    }
  },
};

